import React from 'react';
import ConfirmActionModal from '@components/admin/ConfirmActionModal';
import { formatPersonName } from '@utils/stringUtils';
import { getActivationMessage } from '@utils/contactUserActivation';
import { useContactUserStatusChange } from './useContactUserStatusChange';
import type { ContactUserActivation, UseActivateContactUserOptions } from '@app-types/contactUserStatusChange';

/**
 * Activate an Inactive Contact-User: the button's disabled state and tooltip, and the confirmation.
 * Counterpart of useDeactivateContactUser, shared by the edit form, the detail page header and the list.
 */
export const useActivateContactUser = ({ user, onActivated }: UseActivateContactUserOptions): ContactUserActivation => {
  const statusChange = useContactUserStatusChange({
    user,
    isActive: true,
    successMessage: (result) => getActivationMessage(result.restored),
    failureMessage: 'Failed to activate user',
    onChanged: onActivated
  });

  const { subject } = statusChange;

  const confirmModal = (
    <ConfirmActionModal
      isOpen={statusChange.isConfirmOpen}
      onClose={statusChange.closeConfirm}
      onConfirm={statusChange.confirm}
      title="Activate User"
      message={`Are you sure you want to activate ${subject ? formatPersonName(subject) : 'this user'} (${subject?.email})? The roles and UserID removed when they were deactivated are restored when possible.`}
      isSubmitting={statusChange.isSubmitting}
      actionType="activate"
    />
  );

  return {
    isDisabled: statusChange.isSubmitting,
    tooltip: 'Activate User',
    openConfirm: statusChange.openConfirm,
    confirmModal
  };
};
